import { useCallback, useRef, useState, type MutableRefObject } from 'react';

import {
	ANALYTICS_SOURCE,
	BYLINE_ACTION_SUBJECT_ID,
	EMBEDDED_MENU_SOURCE,
	MENU_CLOSE_ACTION_SUBJECT_ID,
	MENU_SOURCE,
} from '../analytics.ts';
import { useTwgAnalyticsEvents } from '../useTwgAnalyticsEvents.ts';

type BylineConnectionsInteractionOptions = {
	contentId: string;
	openTeamworkGraphPreviewPanel: () => void;
};

type BylineConnectionsInteraction = {
	isOpen: boolean;
	triggerRef: MutableRefObject<HTMLButtonElement | null>;
	handleClose: (isEmbeddedMenu: boolean) => void;
	handleNavigate: () => void;
	handleOpenPreviewPanel: () => void;
	/** `onOpen` runs only when the click opens the menu, not when it closes it. */
	handleToggle: (onOpen?: () => void) => void;
	handlePlaceholderOpenChange: (isOpen: boolean) => void;
};

/** Owns popup transitions, panel opening, and dismissal focus/analytics. */
export const useBylineConnectionsInteraction = ({
	contentId,
	openTeamworkGraphPreviewPanel,
}: BylineConnectionsInteractionOptions): BylineConnectionsInteraction => {
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const [menuState, setMenuState] = useState({ contentId, isOpen: false });
	if (menuState.contentId !== contentId) {
		setMenuState({ contentId, isOpen: false });
	}
	const isCurrentPage = menuState.contentId === contentId;
	const isOpen = isCurrentPage && menuState.isOpen;
	// The popup's focus trap returns focus to the trigger on close by calling
	// `.focus()` (without `preventScroll`), which scrolls the trigger back into
	// view. For a link navigation that fights the in-page scroll, so we disable
	// the trap's focus return (`shouldReturnFocus={false}`) and instead restore
	// focus ourselves on non-navigation closes (Esc / outside click) using
	// `preventScroll` — keeping keyboard a11y without the scroll jump.
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const navigatedRef = useRef(false);
	const handleClose = useCallback(
		(isEmbeddedMenu: boolean) => {
			setMenuState((current) => ({ ...current, isOpen: false }));
			// Navigating away lands here too — the popup fires `onClose` once the panel
			// opens — and that is not a dismissal: it reports its own event, and focus belongs to
			// whatever is opening rather than back on the trigger.
			if (navigatedRef.current) {
				return;
			}
			// What is left is every real dismissal — Esc and outside clicks — so this is
			// one close event per close, mirroring the modals.
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: MENU_CLOSE_ACTION_SUBJECT_ID,
					source: isEmbeddedMenu ? EMBEDDED_MENU_SOURCE : MENU_SOURCE,
				},
			}).fire();
			// Esc / outside-click close: return focus to the trigger without scrolling.
			triggerRef.current?.focus({ preventScroll: true });
		},
		[createAnalyticsEvent],
	);
	const handleNavigate = useCallback(() => {
		navigatedRef.current = true;
		setMenuState((current) => ({ ...current, isOpen: false }));
	}, []);
	const handleOpenPreviewPanel = useCallback(() => {
		navigatedRef.current = true;
		openTeamworkGraphPreviewPanel();
		setMenuState((current) => ({ ...current, isOpen: false }));
	}, [openTeamworkGraphPreviewPanel]);
	const handleToggle = useCallback(
		(onOpen?: () => void) => {
			navigatedRef.current = false;
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: BYLINE_ACTION_SUBJECT_ID,
					source: ANALYTICS_SOURCE,
				},
			}).fire();
			if (!isOpen) {
				onOpen?.();
			}
			setMenuState((current) => ({ ...current, isOpen: !isOpen }));
		},
		[createAnalyticsEvent, isOpen],
	);

	const handlePlaceholderOpenChange = useCallback((isOpen: boolean) => {
		if (isOpen) {
			navigatedRef.current = false;
		}
		setMenuState((current) => ({ ...current, isOpen }));
	}, []);

	return {
		isOpen,
		triggerRef,
		handleClose,
		handleNavigate,
		handleOpenPreviewPanel,
		handleToggle,
		handlePlaceholderOpenChange,
	};
};
